import { InventoryHoldReasonCode } from "~/redux/public/inventory.openApi";
import englishTranslation from "~/translations/english.json";

/** Prefix for i18n keys: `cant induct reason ${reasonCode}`. */
export const CANT_INDUCT_REASON_TRANSLATION_KEY_PREFIX =
  "cant induct reason " as const;

/** Reason codes the backend sends for induction-blocking putaway tasks.
 *  API types use plain `string`, but the backend only emits this closed set. */
export const CANT_INDUCT_REASON_CODES = [
  "product archived",
  "product recalled",
  "product not storable",
  "inventory on hold"
] as const;

export type KnownCantInductReasonCode =
  (typeof CANT_INDUCT_REASON_CODES)[number];

export type CantInductReasonTranslationKey =
  `${typeof CANT_INDUCT_REASON_TRANSLATION_KEY_PREFIX}${KnownCantInductReasonCode}`;

/** Cant-induct translation keys defined in code but missing from `english.json`. */
type MissingCantInductReasonTranslations = Exclude<
  CantInductReasonTranslationKey,
  keyof typeof englishTranslation
>;

/** `true` when every known cant-induct reason has a matching translation key. */
type CantInductReasonTranslationsCompletenessCheck =
  MissingCantInductReasonTranslations extends never
    ? true
    : [
        "Add missing cant-induct reason translations to english.json:",
        MissingCantInductReasonTranslations
      ];

/**
 * Fails the frontend build if a known cant-induct reason is missing its prefixed
 * translation key in `english.json`.
 */
const _cantInductReasonTranslationsAreComplete: CantInductReasonTranslationsCompletenessCheck = true;

/** Marks the check variable as used so TypeScript does not report it as unused. */
void _cantInductReasonTranslationsAreComplete;

/** Hold reason codes we can translate at runtime. Mirrors `InventoryHoldReasonCode`. */
export const INVENTORY_HOLD_REASON_CODES = [
  "bin locked",
  "bin not empty",
  "bin problem",
  "damaged",
  "expired",
  "expiration pad time",
  "external",
  "food safety",
  "out of stock",
  "out of weight threshold",
  "over received",
  "recalled",
  "sanitization status unknown",
  "wrong compartment"
] as const satisfies readonly InventoryHoldReasonCode[];

/** Hold reason codes defined on the API type but missing from `INVENTORY_HOLD_REASON_CODES`. */
type MissingFromInventoryHoldReasonCodesList = Exclude<
  InventoryHoldReasonCode,
  (typeof INVENTORY_HOLD_REASON_CODES)[number]
>;

/** `true` when the list is complete; otherwise an error message naming the missing codes. */
type InventoryHoldReasonCodesListCompletenessCheck =
  MissingFromInventoryHoldReasonCodesList extends never
    ? true
    : [
        "Add missing inventory hold codes to INVENTORY_HOLD_REASON_CODES:",
        MissingFromInventoryHoldReasonCodesList
      ];

/**
 * Fails the frontend build if a new hold reason is added to the API and this list
 * is not updated. Keeps translations in sync with `InventoryHoldReasonCode`.
 */
const _inventoryHoldReasonCodesListIsComplete: InventoryHoldReasonCodesListCompletenessCheck = true;

/** Marks the check variable as used so TypeScript does not report it as unused. */
void _inventoryHoldReasonCodesListIsComplete;

export type CantInductAdditionalDetail = {
  translationKey?: InventoryHoldReasonCode;
  original: string;
};

export type CantInductReasonMapEntry = {
  /** Trimmed backend `reasonCode`, preserving casing for unknown-code display. */
  originalReasonCode: string;
  translationReasonKey?: CantInductReasonTranslationKey;
  additionalDetails?: CantInductAdditionalDetail[];
};

/** Mapped cant-induct reasons for a putaway task, keyed by backend reason code. */
export type CantInductReasonsMap = Partial<
  Record<string, CantInductReasonMapEntry>
>;

export type PutawayTaskCantInductReasons = {
  cantInductReasonsMap: CantInductReasonsMap;
  isPutawayTaskIneligible: boolean;
};
